import React, { useState } from 'react';
import { FieldPath, FieldValues, UseFormReturn } from 'react-hook-form';
import { Person } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography
} from '@mui/material';

import { postS3Files, UploadProgressState } from '@/api/postS3Files';
import {
  getS3UploadErrorMessage,
  getUploadInputAcceptString,
  PROFILE_IMAGE_UPLOAD_PURPOSE,
  SUPPORTED_PROFILE_IMAGE_CONTENT_TYPES
} from '@/api/s3UploadFileTypes';
import { useSnackbar } from '@/hooks/useSnackbar';

import { AvatarS3PhotoKey } from './ui/Avatar/AvatarS3PhotoKey';

import { UploadCircularProgress } from './CircularProgress';

interface ControlledProfilePictureProps<TFields extends FieldValues> {
  name: FieldPath<TFields>;
  formMethods: UseFormReturn<TFields>;
  handleOnChange?: VoidFunction;
}

export function ControlledProfilePicture<TFields extends FieldValues>({
  name,
  formMethods,
  handleOnChange
}: React.PropsWithChildren<ControlledProfilePictureProps<TFields>>) {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = React.useState({} as UploadProgressState);
  const s3PhotoKey = formMethods.watch(name) as string;
  const { showSnackbar } = useSnackbar();
  const profileImageAccept = getUploadInputAcceptString(
    SUPPORTED_PROFILE_IMAGE_CONTENT_TYPES
  );

  const handlePhotoUpload: React.ChangeEventHandler<HTMLInputElement> = async (
    event
  ) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (files.length === 0) {
      return;
    }
    setIsLoading(true);
    try {
      const s3files = await postS3Files(
        files,
        setProgress,
        SUPPORTED_PROFILE_IMAGE_CONTENT_TYPES,
        PROFILE_IMAGE_UPLOAD_PURPOSE
      );
      const s3file = s3files[0];
      if (!s3file) {
        return;
      }
      formMethods.setValue(name, s3file.key as any, { shouldDirty: true });
      handleOnChange?.();
    } catch (error) {
      showSnackbar({
        message: getS3UploadErrorMessage(
          error,
          'Error uploading profile photo. Please try again'
        ),
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoDelete = () => {
    formMethods.setValue(name, null as any, {
      shouldDirty: true
    });
    handleOnChange?.();
  };

  return (
    <Box display="flex" alignItems="center">
      {progress.file && (
        <>
          <UploadCircularProgress size={88} value={progress.loaded} />
          <Typography
            color="var(--palette-semantic-text-caption)"
            fontSize="0.875rem"
            ml={2}
            mb="8px"
          >
            {progress.file.name}
          </Typography>
        </>
      )}
      {!progress.file && (
        <>
          <AvatarS3PhotoKey
            sx={{
              background: 'var(--palette-semantic-background-base)',
              border: s3PhotoKey
                ? 'none'
                : '1px solid var(--palette-primitive-gray-500)',
              width: 88,
              height: 88,
              mr: '12px'
            }}
            s3PhotoKey={s3PhotoKey}
          >
            {isLoading ? (
              <CircularProgress size={26} />
            ) : (
              <Person
                htmlColor="var(--palette-semantic-icon-tertiary)"
                sx={{ fontSize: '3rem' }}
              />
            )}
          </AvatarS3PhotoKey>
          <Stack>
            <Typography
              color="var(--palette-semantic-text-caption)"
              fontSize="0.875rem"
              mb="8px"
            >
              Profile Image
            </Typography>
            <input
              style={{ display: 'none' }}
              type="file"
              accept={profileImageAccept}
              id="profile-picture-input"
              onChange={handlePhotoUpload}
            />
            <Box>
              <Button
                variant="contained"
                size="small"
                onClick={() =>
                  document.getElementById('profile-picture-input')?.click()
                }
              >
                Upload New Image
              </Button>
              {s3PhotoKey && (
                <Button
                  onClick={handlePhotoDelete}
                  variant="contained"
                  color="secondary"
                  size="small"
                  sx={{ ml: 1 }}
                >
                  Remove
                </Button>
              )}
            </Box>
          </Stack>
        </>
      )}
    </Box>
  );
}
